import { useState, useEffect } from "react";
import {
    Grid,
    Typography,
    IconButton,
    Skeleton,
    Divider,
    Stack,
    LinearProgress,
    Fade,
} from "@mui/material";
import { useAppDispatch, useAppSelector } from "helpers/hooks";
import { getApplicantInbox } from "store/slices/employer/applicants/applicant-profile-slice";
import { ICandidateInboxMessage } from "./ApplicantInboxModel";
import { ShPaper } from "shared/SharedStyles/ShSurfaces";
import { PrimaryWordpressThemeColor, ShGreen } from "shared/SharedStyles/styleConstants";
import { format } from "date-fns";
import { ShChip } from "shared/SharedStyles/ShDataDisplay";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import StarIcon from "@mui/icons-material/Star";
import StarBorderIcon from "@mui/icons-material/StarBorder";
import { grey } from "@mui/material/colors";


export const ApplicantInbox = () => {
  const dispatch = useAppDispatch();

  const {
    getApplicantInboxStatus,
    applicantInboxMessages,
    applicantDetails,
  } = useAppSelector((state) => state.employer.applicants.applicantProfile);

  const [selectedMessage, setSelectedMessage] = useState<ICandidateInboxMessage | null>(null);

  useEffect(() => {
    if (applicantDetails?.candidateEmployerJobId) {
      dispatch(getApplicantInbox({ candidateEmployerJobId: applicantDetails.candidateEmployerJobId }));
    }
  }, [dispatch, applicantDetails?.candidateEmployerJobId]);

  useEffect(() => {
    if (
      getApplicantInboxStatus === "success" && applicantInboxMessages &&
      applicantInboxMessages?.length > 0 &&
      !selectedMessage
    ) {
      setSelectedMessage(applicantInboxMessages[0]);
    }
  }, [getApplicantInboxStatus, applicantInboxMessages, selectedMessage]);
  

  const handleMessageClick = (message: ICandidateInboxMessage) => {
    setSelectedMessage(message);
  };

  const stripHtml = (html: string) => {
    const div = document.createElement("div");
    div.innerHTML = html;
    return div.textContent || div.innerText || "";
  };

  const renderInboxList = () => {
    if (getApplicantInboxStatus === "pending") {
      return (
        <>
          <Skeleton variant="rectangular" height={60} />
          <Skeleton variant="rectangular" height={60} />
          <Skeleton variant="rectangular" height={60} />
        </>
      );
    }
  
    return (
      <Fade in timeout={800}>
        <Stack spacing={1}>
          {applicantInboxMessages?.map((message) => {
            const isSelected = selectedMessage?.id === message.id;
            const backgroundColor = isSelected ? grey[100] : "white";
  
            return (
              <ShPaper
                variant="outlined"
                key={message.id}
                onClick={() => handleMessageClick(message)}
                borderStyle='solid'
                borderColor={`${isSelected ? ShGreen : ""}`}
                backgroundColor={backgroundColor}
                cursor="pointer"
                elevation={isSelected ? 2 : 0}
                borderRadius={2}
              >
                <Stack spacing={1}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Stack direction="row" spacing={1} alignItems="center">
                      <MailOutlineIcon color="action" fontSize="small" />
                      <Typography variant="subtitle1" fontWeight="bold" color="text.primary">
                        {message.subject}
                      </Typography>
                    </Stack>
                    <ShChip
                      label={format(new Date(message.received_at), "MMM d, yyyy HH:mm")}
                      size="small"
                      customSize="xs"
                      bgColor={PrimaryWordpressThemeColor}
                      textColor="white"
                    />
                  </Stack>
  
                  <Typography variant="body2" color="text.secondary">
                    {stripHtml(message.body).length > 100
                      ? `${stripHtml(message.body).substring(0, 100)}...`
                      : stripHtml(message.body)}
                  </Typography>
  
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Stack direction="row" spacing={1} alignItems="center">
                      <PersonOutlineIcon fontSize="small" color="disabled" />
                      <Typography variant="caption" color="text.secondary">
                        {applicantDetails?.firstName && applicantDetails?.lastName
                          ? `${applicantDetails.firstName} ${applicantDetails.lastName}`
                          : message.sender}
                      </Typography>
                    </Stack>
                    <IconButton
                      aria-label="favorite"
                      color={message.is_favorite ? "error" : "default"}
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                      }}
                    >
                      {message.is_favorite ? <StarIcon /> : <StarBorderIcon />}
                    </IconButton>
                  </Stack>
                </Stack>
              </ShPaper>
            );
          })}
        </Stack>
      </Fade>
    );
  };
  

  return (
    <>
        {getApplicantInboxStatus === 'pending' && <LinearProgress/>}
        {applicantInboxMessages && applicantInboxMessages?.length === 0 ? 
        <ShPaper variant="outlined">
            <Typography variant="body2" gutterBottom>
                No Emails Sent to Applicant.
            </Typography>
        </ShPaper>
        :
        <Fade in timeout={800}>
            <Grid container spacing={1} padding={2}>
                <Grid item xs={12} md={4}>
                    <ShPaper variant="outlined" elevation={0} overflow="auto">
                        <Typography variant="h6" fontWeight="bold" gutterBottom>
                            Inbox
                        </Typography>
                        {renderInboxList()}
                    </ShPaper>
                </Grid>
                <Grid item xs={12} md={8} minHeight='100vh'>
                    <ShPaper variant="outlined" elevation={0} overflow="auto">
                        {selectedMessage ? (
                            <>
                                <Stack direction='row' justifyContent='space-between' spacing={2}>
                                    <Typography variant="h5" fontWeight="bold" gutterBottom>
                                        {selectedMessage.subject}
                                    </Typography>
                                    <ShChip label={format(new Date(selectedMessage.received_at), "PPPpp")} size="small" customSize="xs" bgColor={PrimaryWordpressThemeColor} textColor="white"/>
                                </Stack>
                                <Typography variant="body1" gutterBottom>
                                    <strong>From:</strong> {selectedMessage.sender}
                                </Typography>
                                <Typography variant="body1" gutterBottom>
                                    <strong>Received:</strong>{" "}
                                    {new Date(selectedMessage.received_at).toLocaleString()}
                                </Typography>
                                <Divider style={{ margin: "16px 0" }} />
                                <Typography variant="body1" dangerouslySetInnerHTML={{ __html: selectedMessage.body }}/>      
                            </>
                        ) : (
                            <Skeleton variant="rectangular" height="100%" />
                        )}
                    </ShPaper>
                </Grid>
            </Grid>
        </Fade>
        }
    </>
  );
};
